/**
 * Employee Filter Bar Component
 * Filters for role, status, team, and search
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SearchIcon, XIcon, Loader2Icon } from 'lucide-react';
import type { EmployeeFilters, EmployeeRole, EmployeeStatus } from '@repo/types';
import { useTeams } from '@/lib/hooks/useTeams';
import { FilterChips, type FilterChip } from '@/components/ui/filter-chips';

const ROLE_LABELS: Record<string, string> = {
  job_scout: 'Job Scout',
  team_lead: 'Team Lead',
  manager: 'Manager',
  admin: 'Admin',
};

interface EmployeeFilterBarProps {
  filters: EmployeeFilters;
  searchQuery: string;
  onFiltersChange: (filters: EmployeeFilters) => void;
  onSearchChange: (query: string) => void;
  onClearFilters?: () => void;
  className?: string;
}

export function EmployeeFilterBar({
  filters,
  searchQuery,
  onFiltersChange,
  onSearchChange,
  onClearFilters,
  className,
}: EmployeeFilterBarProps) {
  // Fetch teams from API
  const { data: teamsData, isLoading: isLoadingTeams } = useTeams(1, 100);
  const teams = teamsData?.data || [];

  const hasActiveFilters =
    filters.role !== 'all' ||
    filters.status !== 'all' ||
    filters.team !== 'all' ||
    searchQuery !== '';

  const handleClearFilters = () => {
    onFiltersChange({ role: 'all', status: 'all', team: 'all' });
    onSearchChange('');
    onClearFilters?.();
  };

  const chips: FilterChip[] = [];
  if (searchQuery)
    chips.push({ key: 'search', label: `Search: ${searchQuery}`, onRemove: () => onSearchChange('') });
  if (filters.role && filters.role !== 'all')
    chips.push({
      key: 'role',
      label: `Role: ${ROLE_LABELS[filters.role] ?? filters.role}`,
      onRemove: () => onFiltersChange({ ...filters, role: 'all' }),
    });
  if (filters.status && filters.status !== 'all')
    chips.push({
      key: 'status',
      label: `Status: ${filters.status}`,
      onRemove: () => onFiltersChange({ ...filters, status: 'all' }),
    });
  if (filters.team && filters.team !== 'all') {
    const team = teams.find((t) => t.id === filters.team);
    chips.push({
      key: 'team',
      label: `Team: ${team?.name ?? filters.team}`,
      onRemove: () => onFiltersChange({ ...filters, team: 'all' }),
    });
  }

  return (
    <div className={`space-y-3 ${className || ''}`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
      {/* Search */}
      <div className="relative flex-1">
        <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search employees by name or email..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Role Filter */}
      <Select
        value={filters.role}
        onValueChange={(value) =>
          onFiltersChange({ ...filters, role: value as EmployeeRole | 'all' })
        }
      >
        <SelectTrigger className="w-full md:w-[180px]">
          <SelectValue placeholder="All Roles" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Roles</SelectItem>
          <SelectItem value="job_scout">Job Scout</SelectItem>
          <SelectItem value="team_lead">Team Lead</SelectItem>
          <SelectItem value="manager">Manager</SelectItem>
          <SelectItem value="admin">Admin</SelectItem>
        </SelectContent>
      </Select>

      {/* Status Filter */}
      <Select
        value={filters.status}
        onValueChange={(value) =>
          onFiltersChange({ ...filters, status: value as EmployeeStatus | 'all' })
        }
      >
        <SelectTrigger className="w-full md:w-[150px]">
          <SelectValue placeholder="All Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Status</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>

      {/* Team Filter */}
      <Select
        value={filters.team}
        onValueChange={(value) => onFiltersChange({ ...filters, team: value })}
        disabled={isLoadingTeams}
      >
        <SelectTrigger className="w-full md:w-[180px]">
          <SelectValue placeholder={isLoadingTeams ? 'Loading teams...' : 'All Teams'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Teams</SelectItem>
          {isLoadingTeams ? (
            <div className="flex items-center justify-center py-2">
              <Loader2Icon className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={handleClearFilters}>
          <XIcon className="mr-2 h-4 w-4" />
          Clear
        </Button>
      )}
      </div>
      <FilterChips chips={chips} />
    </div>
  );
}
